import React from 'react'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import ParametersTable from '../../../../components/views/JobRun/ParametersTable'
import SelectionTable from '../../../../components/views/JobRun/SelectionTable'
import ConfirmDialog from '../../../../components/dialogs/ConfirmDialog'
import Accordion from './Accordion'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import StopIcon from '@mui/icons-material/Stop'
import { styled, TextField } from '@mui/material'

const Header = styled('div')({
  display: 'grid',
  width: '100%',
  borderBottomWidth: '2px',
  borderBottomColor: '#DEDEDE',
  alignItems: 'center',
  gridGap: '12px',
  gridTemplateColumns: 'auto 1fr auto'
})

const Content = styled('div')(({ theme }) => ({
  display: 'flex',
  minHeight: '100%',
  padding: theme.spacing(2),
  gap: theme.spacing(2),
  flexDirection: 'row',
  justifyContent: 'flex-start',
  backgroundColor: '#ECEFF1',
  fontSize: '14px'
}))

export default function CurrentParametersCard ({
  studyIsRunning = false,
  disableActionButton = false,
  tooltip = 'Run Study',
  bases = [],
  aggregators = [],
  parameters = {
    applyIncidenceRateCap: false,
    addMonthlyAggregation: false,
    applySubstandardFlatExtra: false,
    applyExpectedLapseRates: false,
    treatyRestrictions: '',
    policyRestrictions: '',
    observationDate: '',
    periodStartDate: '',
    periodEndDate: ''
  },
  onRunStudy = () => {},
  onAbortStudy = () => {},
  tag = '',
  onHandleTagChange = () => {},
  jobsToBeDeleted = null,
  onDelete = () => {}
}) {
  const [message, setMessage] = React.useState('')
  const handleAbortButtonClick = () => {
    onAbortStudy()
  }

  const handleRunButtonClick = () => {
    if (jobsToBeDeleted != null) {
      jobsToBeDeleted.forEach(job => {
        const newLine = `⚠️Job ${job.id} - Tag: ${job.parameters?.tag} \r\n`
        setMessage(prevMessage => prevMessage + newLine)
      })
      setDialogOpen(true)
    } else {
      onRunStudy()
    }
  }

  const handleConfirmDeleteClick = React.useCallback(() => {
    setDialogOpen(false)
    jobsToBeDeleted.forEach(job => {
      onDelete(job)
    })
    setMessage('')
    onRunStudy()
  }, [jobsToBeDeleted, onDelete, onRunStudy])

  const handleCancelDeleteClick = React.useCallback(() => {
    setDialogOpen(false)
    setMessage('')
  }, [])

  const [dialogOpen, setDialogOpen] = React.useState(false)

  const renderSummarySlot = () => <Header>
    <Typography>
      <strong>Current Parameters</strong>
    </Typography>

    <div></div>

    <Tooltip title={studyIsRunning
      ? 'Abort Study'
      : tooltip}
    >
      <span data-testid='current-param-card-tooltip-button-container'>
        {studyIsRunning
          ? (
          <Button
            data-testid='button-abort'
            variant="outlined"
            color="primary"
            disabled={disableActionButton}
            startIcon={<StopIcon />}
            onClick={handleAbortButtonClick}
          >ABORT STUDY</Button>
            )
          : (
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography variant='h6'>Run tag </Typography>
            </div>
            <TextField value={tag} onChange={onHandleTagChange} style={{ marginLeft: '10px', marginRight: '10px' }}/>
            <Button
              data-testid='button-run'
              color="primary"
              variant="outlined"
              disabled={disableActionButton}
              startIcon={<PlayArrowIcon />}
              onClick={handleRunButtonClick}
            >RUN STUDY
            </Button>
          </div>
            )
        }
      </span>
    </Tooltip>
    <ConfirmDialog
      data-testid='dialog-confirm'
      open={dialogOpen}
      title='Confirm Job Run Deletion?'
      text={'You already have 3 Current Runs, the following non official will be deleted '}
      boldText={message}
      onCancel={handleCancelDeleteClick}
      onConfirm={handleConfirmDeleteClick}
    />
  </Header>

  const renderDetailsSlot = () => <Content>
    <ParametersTable
      data-testid='parameters'
      parameters={parameters}
    />

    <SelectionTable
      data-testid='aggregators'
      title='Aggregators'
      items={aggregators}
    />

    <SelectionTable
      data-testid='bases'
      title='Expected Bases'
      items={bases}
    />
  </Content>

  return <Accordion
    data-testid='accordion'
    summarySlot={renderSummarySlot()}
    detailsSlot={renderDetailsSlot()}
    expanded
  />
}
